<template>
  <o-modal
    ref="modal"
    :title="$t('components.schedule.actions.moveReservation.title')"
    :options="[
      { text: $t('general.cancel'), type: 'cancel', callback: handleCancel },
      { text: $t('general.confirm'), type: 'confirm', callback: handleConfirm },
    ]"
    :loading="loading"
    modal-class="m-2 bg-gray-50 rounded-lg shadow-lg pointer-events-auto overflow-hidden sm:max-w-md sm:w-full h-full max-h-lg dark:bg-gray-900"
    content-class="mx-auto max-w-lg h-full flex flex-col bg-white dark:bg-gray-900"
  >
    <div class="text-left h-full">
      <o-scroll class="h-full">
        <div class="text-base leading-5 text-gray-600 dark:text-gray-300">
          <p class="mb-6">
            {{ $t('components.schedule.actions.moveReservation.info') }}
          </p>

          <o-alert
            v-if="hasConflicts"
            show
            variant="warning"
            :title="$t('components.schedule.actions.moveReservation.conflicts')"
            class="text-left text-sm mb-4"
          >
            <p class="text-sm">
              {{ $t('components.schedule.actions.moveReservation.conflictsInfo') }}
            </p>
          </o-alert>

          <template v-if="reservation">
            <div class="mb-10">
              <p class="text-gray-600 text-xs uppercase tracking-wider mb-2 dark:text-gray-300">
                {{ $t('components.schedule.actions.moveReservation.ogDetails') }}
              </p>
              <div class="flex flex-col divide-y bg-gray-50 border mb-4 rounded-xl px-3 dark:bg-gray-900">
                <div class="py-2 font-medium text-sm">
                  <p>{{ $date.format(latestReservation?.date?.date || reservation?.original?.date?.date, $date.presets.long) }}</p>
                </div>
                <div
                  v-for="service in (latestReservation?.services || reservation?.original?.services)"
                  :key="service.idReservationService"
                  class="flex items-center py-2"
                >
                  <div
                    class="self-stretch w-2 mr-2 rounded"
                    :style="{
                      backgroundColor: '#' + service.service.borderHexColour
                    }"
                  />
                  <div class="flex-shrink-0 text-gray-700 font-semibold mr-4 dark:text-gray-200">
                    <p data-cy="start-time">
                      {{ $date.minutes(service.startTime) }}
                    </p>
                    <p data-cy="finish-time">
                      {{ $date.minutes(service.startTime + service.duration) }}
                    </p>
                  </div>
                  <div class="w-full">
                    <i18n-t keypath="components.schedule.actions.moveReservation.with" tag="p" class="text-gray-700 text-sm font-medium leading-none mb-1 dark:text-gray-200">
                      <template #title>
                        {{ service.service.title }}
                      </template>
                      <template #name>
                        <template v-if="service.employee.displayName">
                          {{ service.employee.displayName }}
                        </template>
                      </template>
                    </i18n-t>
                    <p class="text-sm text-gray-600 dark:text-gray-300 flex flex-wrap leading-none">
                      <span class="mr-4">
                        {{ $t('datetime.increments.mins', { mins: service.duration }, Number(service.duration)) }}
                      </span>
                      <span class="mr-4">
                        {{ service.priceLabel }}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <p class="text-gray-600 text-xs uppercase tracking-wider mb-2 dark:text-gray-300">
                {{ $t('components.schedule.actions.moveReservation.newDetails') }}
              </p>
              <div class="flex flex-col divide-y bg-gray-50 border mb-4 rounded-xl px-3 dark:bg-gray-900">
                <div class="py-2 font-medium text-sm">
                  <p>{{ $date.format(reservation.date.date, $date.presets.long) }}</p>
                </div>
                <div
                  v-for="service in reservation.services"
                  :key="service.idReservationService"
                  class="flex items-center py-2"
                >
                  <div
                    class="self-stretch w-2 mr-2 rounded"
                    :style="{
                      backgroundColor: '#' + service.service.borderHexColour
                    }"
                  />
                  <div class="flex-shrink-0 text-gray-700 font-semibold mr-4 dark:text-gray-200">
                    <p data-cy="start-time">
                      {{ $date.minutes(service.startTime) }}
                    </p>
                    <p data-cy="finish-time">
                      {{ $date.minutes(service.startTime + service.duration) }}
                    </p>
                  </div>
                  <div class="w-full">
                    <i18n-t keypath="components.schedule.actions.moveReservation.with" tag="p" class="text-gray-700 text-sm font-medium leading-none mb-1 dark:text-gray-200">
                      <template #title>
                        {{ service.service.title }}
                      </template>
                      <template #name>
                        <template v-if="service.employee.displayName">
                          {{ service.employee.displayName }}
                        </template>
                      </template>
                    </i18n-t>
                    <p class="text-sm text-gray-600 dark:text-gray-300 flex flex-wrap leading-none">
                      <span class="mr-4">
                        {{ $t('datetime.increments.mins', { mins: service.duration }, Number(service.duration)) }}
                      </span>
                      <span class="mr-4">
                        {{ service.priceLabel }}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div class="border-t pt-6 mt-4 mx-2">
              <checkbox-field
                v-model="sendEmail"
                :label="$t('components.schedule.actions.moveReservation.email')"
                :info="$t('components.schedule.actions.moveReservation.emailInfo')"
              />
              <checkbox-field
                v-model="resetReminder"
                :label="$t('reservation.resetReminders')"
                :info="$t('reservation.resetRemindersInfo')"
              />
            </div>
          </template>
        </div>
      </o-scroll>
    </div>

    <o-dialog
      ref="dialog"
      :title="$t('components.schedule.actions.moveReservation.outdatedTitle')"
      :options="[
        { text: $t('general.continue'), type: 'cancel' },
        { text: $t('components.schedule.actions.moveReservation.refreshSchedule'), type: 'confirm', variant: 'primary', callback: handleRefreshSchedule },
      ]"
    >
      <o-alert
        show
        variant="warning"
        class="text-left text-sm mb-4"
      >
        <p class="text-sm" v-html="$t('components.schedule.actions.moveReservation.outdatedAlert', { dateLabel: latestReservation?.dateLabel })" />
      </o-alert>
      <p>{{ $t('components.schedule.actions.moveReservation.outdatedInfo') }}</p>
    </o-dialog>
  </o-modal>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'

import { ConfirmationRequest } from '@/_api/models/booking'

import {
  OAlert,
} from '@/components/elements'
import {
  CheckboxField,
} from '@/components/fields'

interface Emits {
  (e: 'success', reservation: any): void
  (e: 'cancel'): void
}

const emit = defineEmits<Emits>()

const { $api, $notification, $bus, $i18n } = useNuxtApp()
const { $auth } = useNuxtApp() as any

const modal = ref<any>()
const dialog = ref<any>()

const loading = ref(false)
const reservation = ref<any>(null)
const latestReservation = ref<any>(null)
const validation = ref<any>(null)
const overrideTime = ref(true)
const sendEmail = ref(true)
const resetReminder = ref(true)

const validationServices = computed(() => {
  const services = validation.value?.services ?? validation.value?.data?.services

  return Array.isArray(services) ? services : []
})

const hasConflicts = computed(() => {
  return validationServices.value
    .some((service: any) => service?.conflicts?.length)
})

function open (res: any) {
  reservation.value = res
  fetchReservation()
  handleValidation()

  sendEmail.value = $auth.info?.location?.sendInternalReservationMoveNotifications

  modal.value.open()
}

async function fetchReservation () {
  try {
    const idReservation = reservation.value.idReservation
    const date = reservation.value.date

    if (idReservation && date) {
      const latest = (await $api.reservation.getWithDate(idReservation, date)).data
      const startTimeMatch = latest.startTimeUTC.getTime() === reservation.value.startTimeUTC.getTime()
      if (!startTimeMatch) {
        dialog.value.open()
        latestReservation.value = latest
      }
    }
  } catch (e) {
    console.log(e)
  }
}

async function handleValidation () {
  try {
    const params = {
      customer: reservation.value.customer,
      reservationCustomer: reservation.value.customer,
      customerType: reservation.value.customerType,
    }

    const request = ConfirmationRequest.fromReservation(reservation.value, params)
    const result = await $api.booking.confirmInfo(request)

    if (result) {
      validation.value = result.data ?? result
    }
  } catch (e) {
    console.log(e)
  }
}

async function handleConfirm () {
  loading.value = true

  try {
    const params = {
      customer: reservation.value.customer,
      reservationCustomer: reservation.value.customer,
      customerType: reservation.value.customerType,
    }

    const request = ConfirmationRequest.fromReservation(reservation.value, params)
    const result = (await $api.booking.move(request, {
      overrideTime: overrideTime.value,
      sendEmail: sendEmail.value,
      resetReminder: resetReminder.value,
    })).data

    $notification.toast({
      title: $i18n.t('reservation.moved'),
      text: $i18n.t('components.schedule.actions.moveReservation.successInfo', { label: result.customerInfoLabel }),
      variant: 'success',
    })

    emit('success', result)

    $bus.emit.updated('Reservation', result)

    loading.value = false

    return true
  } catch (e: any) {
    $notification.error({ errors: e?.errors })
    console.log(e)
    loading.value = false
  }
}

function handleCancel () {
  emit('cancel')
  return true
}

function handleRefreshSchedule () {
  $bus.emit.updated('Reservation', reservation.value)
  dialog.value.close()
  modal.value.close()
}

defineExpose({ open })
</script>
